import { useEffect, useState, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import { toolBySlug } from "../data/tools.js";
import ToolIntro, { ToolFaq } from "../components/ToolIntro.jsx";
const EquitySplitTool = lazy(
  () => import("../tools/equitySplit/EquitySplitTool.jsx"),
);
const RunwayTool = lazy(() => import("../tools/runway/RunwayTool.jsx"));
const SafeConvertTool = lazy(
  () => import("../tools/safeConvert/SafeConvertTool.jsx"),
);
const RaisePlannerTool = lazy(
  () => import("../tools/raisePlanner/RaisePlannerTool.jsx"),
);
const ReadinessTool = lazy(
  () => import("../tools/readiness/ReadinessTool.jsx"),
);
const LegalChecklistTool = lazy(
  () => import("../tools/legalChecklist/LegalChecklistTool.jsx"),
);
const MarketSizingTool = lazy(
  () => import("../tools/marketSizing/MarketSizingTool.jsx"),
);
const ValuationTool = lazy(
  () => import("../tools/valuation/ValuationTool.jsx"),
);
const ValuationRevenueTool = lazy(
  () => import("../tools/valuation/ValuationRevenueTool.jsx"),
);
const TermSheetTool = lazy(
  () => import("../tools/termSheet/TermSheetTool.jsx"),
);
const GrowthMetricsTool = lazy(
  () => import("../tools/growthMetrics/GrowthMetricsTool.jsx"),
);
const FundMatchTool = lazy(
  () => import("../tools/fundMatch/FundMatchTool.jsx"),
);
const CapTableTool = lazy(() => import("../tools/capTable/CapTableTool.jsx"));
const UnitEconomicsTool = lazy(
  () => import("../tools/unitEconomics/UnitEconomicsTool.jsx"),
);
const PitchDeckTool = lazy(
  () => import("../tools/pitchDeck/PitchDeckTool.jsx"),
);
const RoundSimulatorTool = lazy(
  () => import("../tools/roundSimulator/RoundSimulatorTool.jsx"),
);
import NotFound from "./NotFound.jsx";
import ToolJourney, { NextSteps } from "../components/ToolJourney.jsx";
import ToolErrorBoundary from "../components/ToolErrorBoundary.jsx";
import ToolGiaiThich from "../components/ToolGiaiThich.jsx";
import XemNhanh from "../components/VideoNgan.jsx";
import { videoCuaTrang } from "../data/videos.js";

/**
 * Bảng ánh xạ toolId → component.
 * Thêm công cụ thứ 13: thêm một dòng ở đây và một document trong danh mục.
 * Không phải sửa router.
 */
const COMPONENTS = {
  "founder-equity-split": EquitySplitTool,
  runway: RunwayTool,
  "safe-convert": SafeConvertTool,
  "raise-planner": RaisePlannerTool,
  "readiness-score": ReadinessTool,
  "legal-checklist": LegalChecklistTool,
  "market-sizing": MarketSizingTool,
  "valuation-preseed": ValuationTool,
  "valuation-revenue": ValuationRevenueTool,
  "fund-match": FundMatchTool,
  "cap-table": CapTableTool,
  "unit-economics-check": UnitEconomicsTool,
  "pitch-deck": PitchDeckTool,
  "round-simulator": RoundSimulatorTool,
  "growth-metrics": GrowthMetricsTool,
  "term-sheet": TermSheetTool,
};

export default function ToolPage() {
  const { slug } = useParams();
  const tool = toolBySlug(slug);

  // Form chỉ dựng sau khi đã chạy trên trình duyệt. Lý do: các công cụ khởi tạo
  // state từ ngày hôm nay, mà ngày lúc build khác ngày lúc người dùng mở trang
  // — render sẵn cả form sẽ gây lệch khi hydrate. Phần tĩnh phía trên vẫn được
  // render sẵn, và đó mới là phần Google cần.
  const [tren, setTren] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const reset = () => setRevision((x) => x + 1);
    window.addEventListener("tool-reset", reset);
    return () => window.removeEventListener("tool-reset", reset);
  }, []);
  useEffect(() => setTren(true), []);
  useEffect(() => {
    const result = document.querySelector(".tool-form .ket-qua");
    if (result) result.id = "ket-qua-cong-cu";
  }, [tren, slug, revision]);

  if (!tool) return <NotFound />;
  const Component = COMPONENTS[tool.toolId];

  return (
    <>
      <ToolIntro tool={tool} />
      <ToolJourney key={tool.toolId} tool={tool} />

      <div className="tool-form">
        {!Component ? (
          <>
            <p>
              Công cụ này đang được xây. Các công cụ đã dùng được nằm ở danh
              mục.
            </p>
            <p>
              <Link className="nut" to="/cong-cu">
                Xem tất cả công cụ
              </Link>
            </p>
          </>
        ) : tren ? (
          <ToolErrorBoundary key={tool.toolId + revision} toolId={tool.toolId}>
            <Suspense fallback={<p role="status">Đang mở công cụ…</p>}>
              <Component key={tool.toolId + revision} tool={tool} />
            </Suspense>
          </ToolErrorBoundary>
        ) : (
          <div className="cho-tai" aria-hidden="true" />
        )}
      </div>
      <XemNhanh ds={videoCuaTrang(`/cong-cu/${tool.slug}`)} path={`/cong-cu/${tool.slug}`} />
      <ToolGiaiThich tool={tool} />
      <NextSteps tool={tool} />
      <ToolFaq tool={tool} />
    </>
  );
}
